import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Menu,
  MenuHeader,
  MenuItem,
  MenuSection,
  MenuSeparator,
  MenuTrigger,
  Text,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { DotsThreeVertical } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { API_URL } from '../../../../../config';
import {
  type RenderType,
  useCreateDashboard,
  useDashboardsList,
} from './useDashboardData';

export type SaveCardPayload = {
  title: string;
  sql: string;
  renderType: RenderType;
  xKey?: string | null;
  yKey?: string | null;
  nameKey?: string | null;
};

const addCardToDashboard = async (
  dashboardId: string,
  payload: SaveCardPayload
): Promise<void> => {
  const res = await fetch(
    `${API_URL}/superadmin/user-events/dashboards/${dashboardId}/cards`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        title: payload.title,
        sql: payload.sql,
        renderType: payload.renderType,
        xKey: payload.xKey ?? null,
        yKey: payload.yKey ?? null,
        nameKey: payload.nameKey ?? null,
        layoutW: 6,
        layoutH: 4,
      }),
    }
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json?.ok === false) {
    throw new Error(json?.error || `Request failed (${res.status})`);
  }
};

type Props = {
  payload: SaveCardPayload;
};

/**
 * Menu attached to a result card on the Explore feed: lets the superadmin
 * save the card's SQL + render config into a new or existing dashboard.
 */
export function SaveToDashboardMenu({ payload }: Props) {
  const dashboardsQuery = useDashboardsList();
  const createDashboard = useCreateDashboard();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [newOpen, setNewOpen] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState(payload.title);

  const dashboards = dashboardsQuery.data ?? [];

  const handleAddToExisting = (dashboardId: string, dashboardTitle: string) => {
    addCardToDashboard(dashboardId, payload)
      .then(() => toast.success(`Added to "${dashboardTitle}"`))
      .catch((err) =>
        toast.error(
          `Failed to add card: ${err instanceof Error ? err.message : String(err)}`
        )
      );
  };

  const handleCreateNew = () => {
    if (!newTitle.trim()) return;
    createDashboard.mutate(
      { title: newTitle.trim() },
      {
        onSuccess: async (dashboard) => {
          try {
            await addCardToDashboard(dashboard.id, payload);
            setNewOpen(false);
            navigate({
              to: '/superadmin/user-events',
              search: { tab: 'dashboards', dashboard: dashboard.id },
            });
          } catch (err) {
            toast.error(
              `Created dashboard but failed to add card: ${err instanceof Error ? err.message : String(err)}`
            );
          }
        },
        onError: (err) =>
          toast.error(`Failed to create dashboard: ${String(err)}`),
      }
    );
  };

  return (
    <>
      <MenuTrigger>
        <Button variant="ghost" size="sm" iconOnly aria-label="Save card">
          <DotsThreeVertical size={16} />
        </Button>
        <Menu
          onAction={(key) => {
            const k = String(key);
            if (k === 'new') {
              setNewTitle(payload.title);
              setNewOpen(true);
              return;
            }
            if (k.startsWith('add:')) {
              const id = k.slice(4);
              const target = dashboards.find((d) => d.id === id);
              handleAddToExisting(id, target?.title ?? 'dashboard');
            }
          }}
        >
          <MenuItem id="new">Save to new dashboard…</MenuItem>
          {dashboards.length > 0 && <MenuSeparator />}
          {dashboards.length > 0 && (
            <MenuSection>
              <MenuHeader>Add to dashboard</MenuHeader>
              {dashboards.map((d) => (
                <MenuItem key={d.id} id={`add:${d.id}`}>
                  {d.title}
                </MenuItem>
              ))}
            </MenuSection>
          )}
        </Menu>
      </MenuTrigger>

      <DialogTrigger isOpen={newOpen} onOpenChange={setNewOpen} size="md">
        <span />
        <Dialog title="New dashboard">
          <DialogContent>
            <Text size="sm" className="text-muted-foreground mb-2 block">
              Saves this card as the first card on a new dashboard.
            </Text>
            <TextField
              label="Dashboard title"
              value={newTitle}
              onChange={setNewTitle}
              autoFocus
            />
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setNewOpen(false)}>
              Cancel
            </Button>
            <Button
              onPress={handleCreateNew}
              isDisabled={!newTitle.trim() || createDashboard.isPending}
            >
              {createDashboard.isPending ? 'Creating…' : 'Create & open'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </>
  );
}
